import { Injectable } from "@angular/core";
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from "@angular/common/http";
import { Observable, throwError, switchMap, catchError, take } from "rxjs";
import { CsrfService } from "../service/csrf.service";
import { AuthService } from "../service/auth.service";

@Injectable()
export class CsrfInterceptor implements HttpInterceptor {
  private isRefreshing = false;

  constructor(
    private csrfService: CsrfService,
    private authService: AuthService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Only add CSRF token to patient-portal POST/PUT/DELETE requests
    if (!this.shouldAddCsrfToken(req)) {
      return next.handle(req);
    }

    // Get the CSRF token
    const csrfToken = this.csrfService.getToken();

    // Clone request and add CSRF token header if available
    const clonedReq = csrfToken
      ? req.clone({
          setHeaders: {
            "X-CSRF-Token": csrfToken,
          },
        })
      : req;

    return next.handle(clonedReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // If we get a 403 error and it's a CSRF error, try to refresh token once
        if (
          error.status === 403 &&
          !this.isRefreshing &&
          this.isCsrfError(error)
        ) {
          this.isRefreshing = true;

          // Try to refresh the CSRF token
          return this.authService.getAuthTokenOnce(true).then((bearerToken) => {
            return this.csrfService
              .fetchToken(bearerToken)
              .pipe(
                take(1),
                switchMap(() => {
                  this.isRefreshing = false;
                  // Retry the request with new token
                  const newCsrfToken = this.csrfService.getToken();
                  const retryReq = newCsrfToken
                    ? req.clone({
                        setHeaders: {
                          "X-CSRF-Token": newCsrfToken,
                        },
                      })
                    : req;
                  return next.handle(retryReq);
                }),
                catchError((refreshError) => {
                  this.isRefreshing = false;
                  return throwError(() => refreshError);
                })
              )
              .toPromise() as Promise<HttpEvent<any>>;
          });
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Check if CSRF token should be added to this request
   */
  private shouldAddCsrfToken(req: HttpRequest<any>): boolean {
    // Only add CSRF to patient-portal endpoints
    if (!req.url.includes("/api/v1/patient-portal/")) {
      return false;
    }

    // Skip CSRF for public endpoints (registration, login, etc.)
    const publicEndpoints = [
      "/registerRequest",
      "/consumeToken",
      "/consumeInviteToken",
      "/sendCode",
      "/createAccount",
      "/csrf-token", // Don't need CSRF to get CSRF token
    ];

    for (const endpoint of publicEndpoints) {
      if (req.url.includes(endpoint)) {
        return false;
      }
    }

    // Add CSRF to POST, PUT, and DELETE requests
    return (
      req.method === "POST" || req.method === "PUT" || req.method === "DELETE"
    );
  }

  /**
   * Check if the error is a CSRF error
   */
  private isCsrfError(error: HttpErrorResponse): boolean {
    if (error.error && typeof error.error === "object") {
      const errorMessage =
        error.error.error?.toLowerCase() || error.error.message?.toLowerCase();
      return (
        errorMessage?.includes("csrf") ||
        errorMessage?.includes("token") ||
        false
      );
    }
    return false;
  }
}
